import { useEffect, useRef, useState, useCallback } from 'react';
import type { HttpClient } from '../client/http';
import type { MeResponse } from '../client/types';
import { migrateAnonStorage } from './storage-migration';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export type User = NonNullable<MeResponse['user']>;

export interface AuthState {
  user: User | null;
  authStatus: AuthStatus;
  refresh: () => Promise<void>;
}

const VISIBILITY_THROTTLE_MS = 60_000;

export function useAuthState(client: HttpClient, appId: string): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const lastFetchRef = useRef<number>(0);
  const prevStatusRef = useRef<AuthStatus>('loading');

  const refresh = useCallback(async () => {
    try {
      const res = await client.request<MeResponse>('GET', '/api/auth/me');
      lastFetchRef.current = Date.now();
      const newUser = res.user;
      const newStatus: AuthStatus = newUser ? 'authenticated' : 'anonymous';
      // Detectar transição anonymous → authenticated pra disparar migração
      if (newUser && (prevStatusRef.current === 'anonymous' || prevStatusRef.current === 'loading')) {
        try { migrateAnonStorage(appId, newUser.id); } catch { /* noop */ }
      }
      setUser(newUser);
      setAuthStatus(newStatus);
      prevStatusRef.current = newStatus;
    } catch {
      // Falha silent → anonymous
      lastFetchRef.current = Date.now();
      setUser(null);
      setAuthStatus('anonymous');
      prevStatusRef.current = 'anonymous';
    }
  }, [client, appId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastFetchRef.current;
      if (elapsed < VISIBILITY_THROTTLE_MS) return;
      refresh();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refresh]);

  return { user, authStatus, refresh };
}
